import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardBody, Button, Spinner, Row, Col, Badge, Table } from "reactstrap";
import { FaArrowLeft, FaMapMarkerAlt, FaEdit, FaEye, FaHeart, FaCalendarAlt } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const PlaceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { AuthReducer: { auth } } = useSelector((state) => state);
  
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaceDetails();
  }, [id]);

  const fetchPlaceDetails = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/edit-place/${id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setPlace(response.data.places || response.data.place || response.data);
    } catch (error) {
      toast.error("Failed to fetch place details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!place) {
    return (
      <div className="text-center mt-5">
        <h4>Place not found</h4>
        <Button color="primary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="d-flex align-items-center mb-3">
            <Button 
              color="link" 
              className="text-white p-0 me-3"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft size={20} />
            </Button>
            <h4 className="text-white mb-0">Place Details</h4>
          </div>
        </div>
      </div>

      {/* Header Card */}
      <Card className="mb-4 text-dark">
        <CardBody>
          <Row>
            <Col md={4}>
              <img
                src={process.env.REACT_APP_IMAGE_URL + `/${place.place_image}`}
                alt={place.place_name}
                className="img-fluid rounded"
                style={{ width: '100%', height: '250px', objectFit: 'cover' }}
              />
            </Col>
            <Col md={8}>
              <div className="d-flex justify-content-between align-items-start mb-3 text-dark">
                <h3 className="fw-bold">{place.place_name}</h3>
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    size="sm"
                    onClick={() => window.open(`/edit-place/${place.id}`, '_blank')}
                  >
                    <FaEdit className="me-1" /> Edit
                  </Button>
                </div>
              </div>
              
              <div className="mb-3 text-dark">
                <Badge color={place.status === 1 ? 'success' : 'danger'} className="me-2">
                  {place.status === 1 ? 'Active' : 'Inactive'}
                </Badge>
                {place.featured === 1 && (
                  <Badge color="warning" className="me-2">Featured</Badge>
                )}
                {place.booking_status === 1 && (
                  <Badge color="info" className="me-2">Bookable</Badge>
                )}
                {place.loved_place === 1 && (
                  <Badge color="danger" className="me-2">
                    <FaHeart className="me-1" /> Loved
                  </Badge>
                )}
              </div>

              <div className="mb-2 text-dark">
                <FaMapMarkerAlt className="text-muted me-2 text-dark" />
                <span>
                  {place.city?.city_name}, {place.state?.state_name}
                </span>
              </div>

              <div className="mb-2">
                <FaCalendarAlt className="text-muted me-2" />
                <span>Created: {new Date(place.createdAt).toLocaleDateString()}</span>
              </div>

              {place.amount > 0 && (
                <div className="mb-2">
                  <strong>Entry Fee: ₹{place.amount}</strong>
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Details Cards */}
      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <CardBody>
              <h5 className="fw-bold mb-3">Categories</h5>
              <Table size="sm">
                <tbody>
                  <tr>
                    <td><strong>Category:</strong></td>
                    <td>
                      {place.place_category_ids?.map(item => item.category?.name).join(', ') || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Sub Category:</strong></td>
                    <td>
                      {place.place_sub_category_ids?.map(item => item.sub_category?.name_english).join(', ') || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Sub Sub Category:</strong></td>
                    <td>
                      {place.place_sub_sub_category_ids?.map(item => item.sub_sub_category?.name_english).join(', ') || 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <CardBody>
              <h5 className="fw-bold mb-3">Location Details</h5>
              <Table size="sm">
                <tbody>
                  <tr>
                    <td><strong>State:</strong></td>
                    <td>{place.state?.state_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>City:</strong></td>
                    <td>{place.city?.city_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Latitude:</strong></td>
                    <td>{place.latitude || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Longitude:</strong></td>
                    <td>{place.longitude || 'N/A'}</td>
                  </tr>
                  {place.map_url && (
                    <tr>
                      <td><strong>Map:</strong></td>
                      <td>
                        <a href={place.map_url} target="_blank" rel="noopener noreferrer">
                          View on Map
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Description */}
      {place.short_description && (
        <Card className="mb-4 text-dark">
          <CardBody>
            <h5 className="fw-bold mb-3">Short Description</h5>
            <div dangerouslySetInnerHTML={{ __html: place.short_description }} />
          </CardBody>
        </Card>
      )}

      {place.description && (
        <Card className="mb-4 text-dark">
          <CardBody>
            <h5 className="fw-bold mb-3">Full Description</h5>
            <div dangerouslySetInnerHTML={{ __html: place.description }} />
          </CardBody>
        </Card>
      )}

      {/* Gallery */}
      {place.gallery_images && place.gallery_images.length > 0 && (
        <Card className="mb-4">
          <CardBody>
            <h5 className="fw-bold mb-3">Gallery</h5>
            <Row>
              {place.gallery_images.map((image, index) => (
                <Col md={3} key={index} className="mb-3">
                  <img
                    src={process.env.REACT_APP_IMAGE_URL + `/${image.gallery_image}`}
                    alt={`Gallery ${index + 1}`}
                    className="img-fluid rounded"
                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                  />
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default PlaceDetails;